import { useContext, useEffect, useMemo, useState } from 'react';

import {
  getDefaultDraftCcRecipients,
  getDefaultDraftToRecipients,
  getDefaultReplyRecipients,
} from 'components/EmailComposer/helpers';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import useSendAsAddresses from 'hooks/useSendAsAddresses';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { Maybe } from 'types/UtilityTypes';

export type UseDraftRecipientsResult = {
  // replyTo kept for callers that still branch on reply vs reply-all preview.
  replyTo: string[];
  replyAllTo: string[];
  to: string[];
  cc: string[];
  bcc: string[];
  setTo: (emails: string[]) => void;
  setCc: (emails: string[]) => void;
  setBcc: (emails: string[]) => void;
};

// Shared To/Cc/Bcc state for Quick Quote and Batch Quote draft replies.
// Defaults to reply-all shape (From + thread To on To; thread Cc + config on Cc).
// Whatever is shown is what Send/Draft apply with replyMode replyAll.
export function useDraftRecipients(): UseDraftRecipientsResult {
  const {
    currentState: { threadFrom, threadTo, threadCc, inboxEmailAddress },
  } = useContext(SidebarStateContext);
  const { quickQuoteConfig } = useServiceFeatures();
  const { addresses: sendAsAddresses } = useSendAsAddresses();
  const inbox = inboxEmailAddress ?? '';

  const [toOverride, setToOverride] = useState<Maybe<string[]>>(null);
  const [ccOverride, setCcOverride] = useState<Maybe<string[]>>(null);
  const [bccOverride, setBccOverride] = useState<Maybe<string[]>>(null);

  // Stable keys so reference-equal array recreations do not clear edits.
  const threadToKey = (threadTo ?? []).join('\0');
  const threadCcKey = (threadCc ?? []).join('\0');

  useEffect(() => {
    setToOverride(null);
    setCcOverride(null);
    setBccOverride(null);
  }, [threadFrom, threadToKey, threadCcKey, inbox]);

  const replyTo = useMemo(
    () =>
      getDefaultReplyRecipients(
        threadFrom ? [threadFrom] : [],
        inbox,
        sendAsAddresses
      ),
    [threadFrom, inbox, sendAsAddresses]
  );

  const replyAllTo = useMemo(() => {
    const defaults = getDefaultDraftToRecipients(
      threadFrom,
      threadTo,
      inbox,
      sendAsAddresses
    );
    if (defaults.length > 0) {
      return defaults;
    }

    // Self/self test mail (or only-self participants) filters to []. Graph
    // createReply still targets the message sender — keep that in the To preview.
    if (threadFrom) {
      return [threadFrom];
    }

    return [];
  }, [threadFrom, threadTo, inbox, sendAsAddresses]);

  const defaultCc = useMemo(
    () =>
      getDefaultDraftCcRecipients(
        threadCc,
        quickQuoteConfig?.cc,
        inbox,
        sendAsAddresses
      ),
    [threadCc, quickQuoteConfig?.cc, inbox, sendAsAddresses]
  );

  const defaultBcc = useMemo(
    () => quickQuoteConfig?.bcc ?? [],
    [quickQuoteConfig?.bcc]
  );

  const result: UseDraftRecipientsResult = {
    replyTo,
    replyAllTo,
    to: toOverride ?? replyAllTo,
    cc: ccOverride ?? defaultCc,
    bcc: bccOverride ?? defaultBcc,
    setTo: (emails: string[]) => {
      setToOverride(emails);
    },
    setCc: (emails: string[]) => {
      setCcOverride(emails);
    },
    setBcc: (emails: string[]) => {
      setBccOverride(emails);
    },
  };

  return result;
}
